/**
 * Table-of-contents scrollspy.
 *
 * Markup comes from Elements/content/toc.php; the rail is styled in
 * _v2/css/src/components.css. This module only maintains two classes:
 *
 *   .active  the section you are in — exactly one link at a time
 *   .done    every link above it, so the gold spine shows how much is behind you
 *
 * The headings it watches are the `#section-N` ids that
 * Newsstudios/view.ctp injects into the article body. Both the ids and the
 * links come from the same pass over the content, so a link can never point at
 * an anchor that does not exist — but this still resolves each one and skips
 * what it cannot find, because the body is editor HTML and an editor can delete
 * a heading between the two.
 */
import { qsa } from './dom.js';

export function setupToc() {
  const links = qsa('[data-toc] [data-toc-link]');
  if (!links.length) return;

  // No IntersectionObserver: the rail still works as a list of links, which is
  // its primary job. Only the progress read-out is lost.
  if (!('IntersectionObserver' in window)) return;

  const byId = new Map();
  const targets = [];

  for (const link of links) {
    const id = (link.getAttribute('href') || '').slice(1);
    if (!id) continue;
    const target = document.getElementById(id);
    if (!target) continue;
    byId.set(id, link);
    targets.push(target);
  }

  if (!targets.length) return;

  const setActive = (index) => {
    links.forEach((link, i) => {
      link.classList.toggle('done', i < index);
      link.classList.toggle('active', i === index);
    });
  };

  /*
   * Start on the first section rather than blank.
   *
   * The observer only fires once a heading crosses the band below, so at the
   * top of the article no heading has been seen yet and the rail would sit
   * empty — which reads as broken rather than as "not started". You are at the
   * top of the article, so the first section is where you are.
   */
  setActive(0);

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const link = byId.get(entry.target.id);
        if (!link) continue;
        setActive(links.indexOf(link));
      }
    },
    /*
     * A band across the middle of the viewport, not the whole of it. With the
     * default margins several headings are on screen at once in a short
     * article and the last one to fire wins, which makes the coin jump around
     * while you scroll. -30%/-60% leaves a ~10% band: one heading is in it at
     * a time.
     */
    { rootMargin: '-30% 0px -60% 0px' }
  );

  targets.forEach((target) => observer.observe(target));

  /*
   * Clicking a link should light it immediately.
   *
   * The scroll is smooth (`scroll-behavior` in components.css), so the
   * observer's confirmation is up to a second behind the tap — long enough
   * that the control feels unresponsive. This is optimistic and the observer
   * corrects it on arrival.
   */
  for (const [index, link] of links.entries()) {
    link.addEventListener('click', () => setActive(index));
  }

  return observer;
}
